import { Field, useFormikContext } from 'formik';
import { ReactElement, useEffect } from 'react';
import FormDropdown from 'src/components/FormInputs/FormDropdown/FormDropdown';
import FormInputLabel from 'src/components/FormInputs/FormInputLabel/FormInputLabel';
import FormTextbox from 'src/components/FormInputs/FormTextbox/FormTextbox';
import MeasureFieldLabel from 'src/components/FormInputs/MeasureFieldLabel/MeasureFieldLabel';
import Panel from 'src/components/Panel/Panel';
import { useSealOfBiliteracyContext } from 'src/contexts/SealOfBiliteracyContext';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  selectSealOfBiliteracyCurrentSchoolYear,
  selectSealOfBiliteracyForm,
} from 'src/redux/slices/sealOfBiliteracySlice';
import { fetchSealOfBiliteracyFormViewModel } from 'src/redux/thunks/sealOfBiliteracyThunks';
import { PanelType } from 'src/types/PanelType';
import { FormDropdownChangeEventConfig } from 'src/types/propTypes/FormDropdownPropTypes';
import { SealOfBiliteracyMeasureModel } from 'src/types/SealOfBiliteracyOptionsModel';
import { isEditableSchoolYear } from 'src/utilities/sealOfBiliteracyUtilities';
import { isDistrictUser, isStateAdmin } from 'src/utilities/userUtilities';

// Type definitions for seal calculation
type SealLevel = 'Gold' | 'Silver' | 'No Seal Awarded' | '';
type MeasureData = {
  measureName: string;
  measureValue: string;
};

const SOBAssessmentPanel = ({
  schoolYear,
  disabled = false,
}: {
  schoolYear: string | undefined;
  disabled?: boolean;
}): ReactElement => {
  const dispatch = useAppDispatch();
  const formViewModel = useAppSelector(selectSealOfBiliteracyForm);
  const currentSchoolYear = useAppSelector(
    selectSealOfBiliteracyCurrentSchoolYear
  );

  const {
    selectedEnglishAssessment,
    selectedWorldLanguageAssessment,
    setSelectedEnglishAssessment,
    setSelectedWorldLanguageAssessment,
    currentEnglishAssessment,
    currentWorldLanguageAssessment,
    setCurrentEnglishAssessment,
    setCurrentWorldLanguageAssessment,
    dynamicValidationSchema,
    hasScoreEdits,
    setHasScoreEdits,
    setIsPortfolioAssessment,
  } = useSealOfBiliteracyContext();

  useEffect(() => {
    if (!formViewModel) {
      dispatch(fetchSealOfBiliteracyFormViewModel());
    }
  }, [dispatch, formViewModel]);

  const isReadOnlyUser = (): boolean => {
    if (isStateAdmin() || isDistrictUser()) return false;
    else return true;
  };

  const englishAssessmentTypeOptions =
    formViewModel?.englishAssessmentTypeOptions || [];
  const worldLanguageAssessmentTypeOptions =
    formViewModel?.worldLanguageAssessmentTypeOptions || [];
  const assessmentOptions = formViewModel?.assessmentOptions || [];

  const getAssessmentByNameAndType = (
    assessmentName: string,
    isEnglishAssessment: boolean
  ): typeof assessmentOptions[number] | undefined => {
    return assessmentOptions.find(
      (assessment) =>
        assessment.assessmentName === assessmentName &&
        assessment.isEnglishAssessment === isEnglishAssessment
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formikContext = useFormikContext<any>();

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  const calculateFieldSeal = (measure: any, userValue: string): SealLevel => {
    if (!userValue || userValue.trim() === '') {
      return 'No Seal Awarded';
    }

    if (measure.measureType === 'NumericRange') {
      const numericValue = parseFloat(userValue);
      if (isNaN(numericValue)) {
        return 'No Seal Awarded';
      }

      // Check Gold range
      const goldLow = parseFloat(measure.goldLow);
      const goldHigh = parseFloat(measure.goldHigh);
      if (
        !isNaN(goldLow) &&
        !isNaN(goldHigh) &&
        numericValue >= goldLow &&
        numericValue <= goldHigh
      ) {
        return 'Gold';
      }

      // Check Silver range
      const silverLow = parseFloat(measure.silverLow);
      const silverHigh = parseFloat(measure.silverHigh);
      if (
        !isNaN(silverLow) &&
        !isNaN(silverHigh) &&
        numericValue >= silverLow &&
        numericValue <= silverHigh
      ) {
        return 'Silver';
      }
    } else if (measure.measureType === 'OptionSet') {
      if (measure.goldMeasures && measure.goldMeasures.includes(userValue)) {
        return 'Gold';
      }

      if (
        measure.silverMeasures &&
        measure.silverMeasures.includes(userValue)
      ) {
        return 'Silver';
      }
    }

    return 'No Seal Awarded';
  };

  // Helper function to calculate seal for an assessment (English or World Language)
  const calculateAssessmentSeal = (
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    assessment: any,
    measures: MeasureData[]
  ): SealLevel => {
    if (!assessment || !assessment.measures || !measures) {
      return '';
    }

    const sealCalculationMeasures = assessment.measures.filter(
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      (measure: any) => measure.calculateSealAward === true
    );

    if (sealCalculationMeasures.length === 0) {
      return '';
    }

    const requiredMeasures = sealCalculationMeasures.filter(
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      (measure: any) => measure.isRequired === true
    );

    for (const requiredMeasure of requiredMeasures) {
      const measureData = measures.find(
        (m) => m.measureName === requiredMeasure.measureName
      );
      if (
        !measureData ||
        !measureData.measureValue ||
        measureData.measureValue.trim() === ''
      ) {
        return '';
      }
    }

    const fieldSeals: SealLevel[] = [];

    for (const measure of sealCalculationMeasures) {
      const measureData = measures.find(
        (m) => m.measureName === measure.measureName
      );

      if (
        measureData &&
        measureData.measureValue &&
        measureData.measureValue.trim() !== ''
      ) {
        const fieldSeal = calculateFieldSeal(measure, measureData.measureValue);
        fieldSeals.push(fieldSeal);
      }
    }

    if (fieldSeals.length === 0) {
      return '';
    }

    // Apply lowest seal logic: if any is "No Seal Awarded", result is "No Seal Awarded"
    // If any is "Silver" (and none are "No Seal Awarded"), result is "Silver"
    // Otherwise, result is "Gold"
    if (fieldSeals.includes('No Seal Awarded')) {
      return 'No Seal Awarded';
    } else if (fieldSeals.includes('Silver')) {
      return 'Silver';
    } else {
      return 'Gold';
    }
  };

  const updateSealAwardedStatus = (): void => {
    const formValues = formikContext.values;
    const hasEnglishAssessment =
      formValues.englishAssessmentName &&
      formValues.englishAssessmentName.trim() !== '';
    const hasLanguageAssessment =
      formValues.languageAssessmentName &&
      formValues.languageAssessmentName.trim() !== '';

    if (!hasEnglishAssessment && !hasLanguageAssessment) {
      formikContext.setFieldValue('sealAwarded', '', false);
      return;
    }

    if (
      (hasEnglishAssessment && !hasLanguageAssessment) ||
      (!hasEnglishAssessment && hasLanguageAssessment)
    ) {
      formikContext.setFieldValue('sealAwarded', '', false);
      return;
    }

    let englishSeal: SealLevel = '';
    let worldLanguageSeal: SealLevel = '';

    if (hasEnglishAssessment && currentEnglishAssessment) {
      englishSeal = calculateAssessmentSeal(
        currentEnglishAssessment,
        formValues.englishAssessmentMeasures || []
      );
    }

    if (hasLanguageAssessment && currentWorldLanguageAssessment) {
      worldLanguageSeal = calculateAssessmentSeal(
        currentWorldLanguageAssessment,
        formValues.languageAssessmentMeasures || []
      );
    }

    if (englishSeal === '' || worldLanguageSeal === '') {
      formikContext.setFieldValue('sealAwarded', '', false);
      return;
    }

    let finalSeal: string;
    if (
      englishSeal === 'No Seal Awarded' ||
      worldLanguageSeal === 'No Seal Awarded'
    ) {
      finalSeal = 'No Seal Awarded';
    } else if (englishSeal === 'Silver' || worldLanguageSeal === 'Silver') {
      finalSeal = 'Silver';
    } else {
      finalSeal = 'Gold';
    }

    formikContext.setFieldValue('sealAwarded', finalSeal, false);
  };

  const trackScoreEdit = (): void => {
    if (!hasScoreEdits) {
      setHasScoreEdits(true);
    }
  };

  const checkPortfolioAssessment = (assessmentName: string): boolean => {
    return assessmentName.toLowerCase().includes('portfolio');
  };

  useEffect(() => {
    if (isReadOnlyUser()) return;
    const formValues = formikContext.values;
    const hasEnglishAssessment =
      formValues.englishAssessmentName &&
      formValues.englishAssessmentName.trim() !== '';
    const hasLanguageAssessment =
      formValues.languageAssessmentName &&
      formValues.languageAssessmentName.trim() !== '';

    if (hasEnglishAssessment || hasLanguageAssessment) {
      updateSealAwardedStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formikContext.values.englishAssessmentMeasures,
    formikContext.values.languageAssessmentMeasures,
  ]);

  useEffect(() => {
    if (
      formViewModel &&
      formViewModel.assessmentOptions &&
      !selectedEnglishAssessment &&
      !selectedWorldLanguageAssessment
    ) {
      const formValues = formikContext.values;

      if (formValues.englishAssessmentName) {
        setSelectedEnglishAssessment(formValues.englishAssessmentName);
        const assessment = getAssessmentByNameAndType(
          formValues.englishAssessmentName,
          true
        );
        setCurrentEnglishAssessment(assessment);

        const isPortfolio = checkPortfolioAssessment(
          formValues.englishAssessmentName
        );
        setIsPortfolioAssessment(isPortfolio);

        if (
          !isReadOnlyUser() &&
          assessment &&
          assessment.measures &&
          (!formValues.englishAssessmentMeasures ||
            formValues.englishAssessmentMeasures.length === 0)
        ) {
          const initialMeasures = assessment.measures.map(
            (measure: SealOfBiliteracyMeasureModel) => ({
              measureName: measure.measureName,
              measureValue: '',
            })
          );
          formikContext.setFieldValue(
            'englishAssessmentMeasures',
            initialMeasures,
            false
          );
          setTimeout(() => {
            formikContext.validateForm();
          }, 0);
        }
      }

      if (formValues.languageAssessmentName) {
        setSelectedWorldLanguageAssessment(formValues.languageAssessmentName);
        const assessment = getAssessmentByNameAndType(
          formValues.languageAssessmentName,
          false
        );
        setCurrentWorldLanguageAssessment(assessment);

        const isPortfolio = checkPortfolioAssessment(
          formValues.languageAssessmentName
        );
        setIsPortfolioAssessment(isPortfolio);

        if (
          !isReadOnlyUser() &&
          !formValues.languageAssessmentCode &&
          assessment &&
          assessment.allowedLanguages &&
          assessment.allowedLanguages.length > 0
        ) {
          const defaultLanguage = assessment.allowedLanguages[0];
          formikContext.setFieldValue(
            'languageAssessmentCode',
            defaultLanguage.languageCode,
            false
          );
        }

        if (
          !isReadOnlyUser() &&
          assessment &&
          assessment.measures &&
          (!formValues.languageAssessmentMeasures ||
            formValues.languageAssessmentMeasures.length === 0)
        ) {
          const initialMeasures = assessment.measures.map(
            (measure: SealOfBiliteracyMeasureModel) => ({
              measureName: measure.measureName,
              measureValue: '',
            })
          );
          formikContext.setFieldValue(
            'languageAssessmentMeasures',
            initialMeasures,
            false
          );
          setTimeout(() => {
            formikContext.validateForm();
          }, 0);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formViewModel]);

  const handleEnglishAssessmentChange = async (
    props: FormDropdownChangeEventConfig
  ): Promise<void> => {
    const value = Array.isArray(props.value) ? props.value[0] : props.value;
    const assessmentName = value || '';
    setSelectedEnglishAssessment(assessmentName);

    const assessment = getAssessmentByNameAndType(assessmentName, true);
    setCurrentEnglishAssessment(assessment);

    const isPortfolio = checkPortfolioAssessment(assessmentName);
    setIsPortfolioAssessment(isPortfolio);

    if (assessment && assessment.measures) {
      const initialMeasures = assessment.measures.map(
        (measure: SealOfBiliteracyMeasureModel) => ({
          measureName: measure.measureName,
          measureValue: '',
        })
      );
      formikContext.setFieldValue(
        'englishAssessmentMeasures',
        initialMeasures,
        false
      );
      setTimeout(() => {
        formikContext.validateForm();
      }, 0);
    } else {
      formikContext.setFieldValue('englishAssessmentMeasures', [], true);
    }
  };

  const handleWorldLanguageAssessmentChange = async (
    props: FormDropdownChangeEventConfig
  ): Promise<void> => {
    const value = Array.isArray(props.value) ? props.value[0] : props.value;
    const assessmentName = value || '';
    setSelectedWorldLanguageAssessment(assessmentName);

    const assessment = getAssessmentByNameAndType(assessmentName, false);
    setCurrentWorldLanguageAssessment(assessment);

    const isPortfolio = checkPortfolioAssessment(assessmentName);
    setIsPortfolioAssessment(isPortfolio);

    if (
      assessment &&
      assessment.allowedLanguages &&
      assessment.allowedLanguages.length > 0
    ) {
      const defaultLanguage = assessment.allowedLanguages[0];
      formikContext.setFieldValue(
        'languageAssessmentCode',
        defaultLanguage.languageCode,
        true
      );
    } else {
      formikContext.setFieldValue('languageAssessmentCode', '', true);
    }

    if (assessment && assessment.measures) {
      const initialMeasures = assessment.measures.map(
        (measure: SealOfBiliteracyMeasureModel) => ({
          measureName: measure.measureName,
          measureValue: '',
        })
      );
      formikContext.setFieldValue(
        'languageAssessmentMeasures',
        initialMeasures,
        false
      );
      setTimeout(() => {
        formikContext.validateForm();
      }, 0);
    } else {
      formikContext.setFieldValue('languageAssessmentMeasures', [], true);
    }
  };

  const renderMeasureFields = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    measures: any[],
    prefix: string
  ): (JSX.Element | null)[] => {
    return measures.map((measure, index) => {
      const fieldName = `${prefix}Measures[${index}].measureValue`;
      const measureNameField = `${prefix}Measures[${index}].measureName`;

      if (!isReadOnlyUser() && !formikContext.values[`${prefix}Measures`]) {
        formikContext.setFieldValue(`${prefix}Measures`, [], false);
      }

      if (!isReadOnlyUser() && !formikContext.values[`${prefix}Measures`]?.[index]) {
        formikContext.setFieldValue(
          `${prefix}Measures[${index}]`,
          {
            measureName: measure.measureName,
            measureValue: '',
          },
          false
        );
      }

      if (measure.measureType === 'OptionSet') {
        return (
          <div
            key={index}
            className="field-row"
            style={{ flex: '0 1 250px', minWidth: '200px' }}
          >
            {/* Hidden field for measure name */}
            <Field
              type="hidden"
              name={measureNameField}
              value={measure.measureName}
              disabled={
                disabled ||
                isReadOnlyUser() ||
                !isEditableSchoolYear(schoolYear, currentSchoolYear)
              }
            />
            <MeasureFieldLabel
              displayName={measure.measureName}
              isRequired={measure.isRequired}
              field={fieldName}
              disabled={
                disabled ||
                isReadOnlyUser() ||
                !isEditableSchoolYear(schoolYear, currentSchoolYear)
              }
            />
            <FormDropdown
              field={fieldName}
              options={measure.measureSetOptions || []}
              validationSchema={dynamicValidationSchema}
              onChangeEvent={async () => {
                trackScoreEdit();
              }}
              disabled={
                disabled ||
                isReadOnlyUser() ||
                !isEditableSchoolYear(schoolYear, currentSchoolYear)
              }
            />
          </div>
        );
      } else if (measure.measureType === 'NumericRange') {
        return (
          <div
            key={index}
            className="field-row"
            style={{ flex: '0 1 250px', minWidth: '200px' }}
          >
            {/* Hidden field for measure name */}
            <Field
              type="hidden"
              name={measureNameField}
              value={measure.measureName}
              disabled={
                disabled ||
                isReadOnlyUser() ||
                !isEditableSchoolYear(schoolYear, currentSchoolYear)
              }
            />
            <MeasureFieldLabel
              displayName={measure.measureName}
              isRequired={measure.isRequired}
              field={fieldName}
              disabled={
                disabled ||
                isReadOnlyUser() ||
                !isEditableSchoolYear(schoolYear, currentSchoolYear)
              }
            />
            <FormTextbox
              displayName=""
              field={fieldName}
              validationSchema={dynamicValidationSchema}
              onChangeEvent={async () => {
                trackScoreEdit();
              }}
              disabled={
                disabled ||
                isReadOnlyUser() ||
                !isEditableSchoolYear(schoolYear, currentSchoolYear)
              }
            />
          </div>
        );
      }
      return null;
    });
  };

  return (
    <div
      data-testid="seal-of-biliteracy-input-child"
      className="data-input--seal-of-biliteracy--input"
    >
      <Panel panelType={PanelType.INFO} heading="Seal of Biliteracy">
        <FormInputLabel displayName="English" field={''} />
        <div className="field-row field-row-1-1-1">
          <FormDropdown
            displayName="Test Type"
            field="englishAssessmentName"
            options={englishAssessmentTypeOptions || []}
            disabled={
              disabled ||
              isReadOnlyUser() ||
              !isEditableSchoolYear(schoolYear, currentSchoolYear)
            }
            classConfig={{ labelClasses: 'required' }}
            validationSchema={dynamicValidationSchema}
            onChangeEvent={handleEnglishAssessmentChange}
          />
        </div>

        {/* Dynamic English Assessment Measures */}
        {currentEnglishAssessment && currentEnglishAssessment.measures && (
          <>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                width: '100%',
                alignItems: 'flex-start',
              }}
            >
              {renderMeasureFields(
                currentEnglishAssessment.measures,
                'englishAssessment'
              )}
            </div>
          </>
        )}

        {/* Clear separation between English and World Language sections */}
        <div style={{ clear: 'both', marginTop: '20px', display: 'block' }}>
          <FormInputLabel displayName="World Language" field={''} />
        </div>
        <div className="field-row field-row-1-1-1">
          <FormDropdown
            displayName="Assessment Type"
            field="languageAssessmentName"
            options={worldLanguageAssessmentTypeOptions || []}
            disabled={
              disabled ||
              isReadOnlyUser() ||
              !isEditableSchoolYear(schoolYear, currentSchoolYear)
            }
            classConfig={{ labelClasses: 'required' }}
            validationSchema={dynamicValidationSchema}
            onChangeEvent={handleWorldLanguageAssessmentChange}
          />
          {/* Language Selection Dropdown - Show right next to Assessment Type */}
          {currentWorldLanguageAssessment && (
            <FormDropdown
              displayName="Language"
              field="languageAssessmentCode"
              options={
                currentWorldLanguageAssessment.allowedLanguages?.map(
                  (lang) => ({
                    value: lang.languageCode,
                    text: lang.languageCodeDescription,
                  })
                ) || []
              }
              classConfig={{ labelClasses: 'required' }}
              validationSchema={dynamicValidationSchema}
              disabled={
                disabled ||
                isReadOnlyUser() ||
                !isEditableSchoolYear(schoolYear, currentSchoolYear)
              }
            />
          )}
        </div>

        {/* Dynamic World Language Assessment Measures */}
        {currentWorldLanguageAssessment &&
          currentWorldLanguageAssessment.measures && (
            <>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '12px',
                  width: '100%',
                  alignItems: 'flex-start',
                }}
              >
                {renderMeasureFields(
                  currentWorldLanguageAssessment.measures,
                  'languageAssessment'
                )}
              </div>
            </>
          )}
        <div style={{ clear: 'both', marginTop: '20px', display: 'block' }} />

        {/* SealAwarded field - disabled text box */}
        <div className="field-row field-row-1-1-1">
          <FormTextbox
            displayName="Seal Awarded"
            field="sealAwarded"
            disabled={true}
            validationSchema={dynamicValidationSchema}
          />
        </div>
      </Panel>
    </div>
  );
};

export default SOBAssessmentPanel;
